import { z } from 'zod'
import type { TDictionaryItem } from '../../../../api/types'
import { FIELD_CANNOT_BE_EMPTY } from '../../../../consts/validationErrors'
import type { TeamDescription } from '../../../../types/entitys/teams'
import {
  buildIdArrayFromProfileNames,
  dictionaryIdArraySchema,
  mapDictionaryIdsToNames,
} from '../../../../utils/dictionary/formHelpers'

const parseLines = (s: string) =>
  s
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

const linesFieldSchema = (emptyMsg = 'Добавьте хотя бы один элемент') =>
  z
    .string()
    .trim()
    .superRefine((s, ctx) => {
      const lines = parseLines(s)
      if (lines.length < 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: emptyMsg })
        return
      }
      for (const line of lines) {
        if (line.length < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Минимально 2 символа',
          })
          return
        }
        if (line.length > 500) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Максимально 500 символов в каждой строке',
          })
          return
        }
      }
    })

const COVER_REQUIRED = 'Загрузите обложку команды'

export const teamFormSchema = z.object({
  title: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .min(2, { message: 'Минимально 2 символа' })
    .max(240, { message: 'Максимально 240 символов' }),
  city: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .min(2, { message: 'Минимально 2 символа' })
    .max(255, { message: 'Максимально 255 символов' }),
  status: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .min(2, { message: 'Минимально 2 символа' })
    .max(500, { message: 'Максимально 500 символов' }),
  descriptionShort: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .min(2, { message: 'Минимально 2 символа' })
    .max(500, { message: 'Максимально 500 символов' }),
  region: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY }),
  image: z.union(
    [z.instanceof(File), z.string().trim().min(1, { message: COVER_REQUIRED })],
    { required_error: COVER_REQUIRED },
  ),
  directionIds: dictionaryIdArraySchema('Добавьте хотя бы одно направление из справочника'),
  goalsText: linesFieldSchema(),
  resourcesText: linesFieldSchema(),
  needsText: linesFieldSchema(),
})

export type TeamFormValues = z.infer<typeof teamFormSchema>

export const teamToFormValues = (
  team: TeamDescription,
  focuses: TDictionaryItem[]
) => ({
  title: team.title,
  city: team.city,
  status: team.status,
  descriptionShort: team.descriptionShort ?? '',
  region: team.region ?? '',
  image: team.image ?? '',
  directionIds: buildIdArrayFromProfileNames(team.directions ?? [], focuses),
  goalsText: Array.isArray(team.goals) ? team.goals.join('\n') : '',
  resourcesText: Array.isArray(team.resources) ? team.resources.join('\n') : '',
  needsText: Array.isArray(team.needs) ? team.needs.join('\n') : '',
})

export const teamFormToPayload = (data: TeamFormValues, focuses: TDictionaryItem[]) => ({
  title: data.title,
  city: data.city,
  status: data.status,
  descriptionShort: data.descriptionShort,
  descriptionFull: '',
  region: data.region,
  image: typeof data.image === 'string' ? data.image : '',
  directions: mapDictionaryIdsToNames(data.directionIds, focuses),
  goals: parseLines(data.goalsText),
  projects: [] as string[],
  resources: parseLines(data.resourcesText),
  needs: parseLines(data.needsText),
})
